import { useState, useEffect } from "react";
import GameLayout from "../components/GameLayout";
import API from "../lib/api";
import { Trophy, Medal, Calendar, Users, Gift } from "lucide-react";

const RANK_COLORS = ["#FFD600", "#C0C0C0", "#CD7F32"];

export default function LeaderboardPage() {
  const [data, setData] = useState({});
  const [archiveTab, setArchiveTab] = useState("weekly_coins");

  useEffect(() => { API.get("/leaderboard").then(r => setData(r.data)).catch(() => {}); }, []);

  const leaders = data.leaderboard || [];
  const weeklyRankings = data.weekly_rankings || [];
  const monthlyRefs = data.monthly_referrals || [];
  const archive = data.archive || [];
  const weeklyArchive = archive.filter(a => a.type === "weekly_coins");
  const monthlyArchive = archive.filter(a => a.type === "monthly_referrals");
  const weeklyRewards = data.weekly_config?.rewards || [];
  const monthlyRewards = data.monthly_config?.rewards || [];

  return (
    <GameLayout title="Leaderboard">
      <div className="space-y-6" data-testid="leaderboard-page">
        {/* Top 3 Podium */}
        {leaders.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 items-end">
            {[1, 0, 2].map((idx) => {
              const l = leaders[idx];
              const isFirst = idx === 0;
              return (
                <div key={idx} className={`text-center ${isFirst ? "order-2" : idx === 1 ? "order-1" : "order-3"}`}>
                  <div className={`bg-[#131722] rounded-2xl border border-[#2A3241]/40 p-5 ${isFirst ? "pb-8 -mb-3 relative z-10 border-[#FFD600]/30" : ""}`}>
                    <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${isFirst ? "bg-[#FFD600]/20 ring-2 ring-[#FFD600]" : "bg-[#1C212E]"}`}>
                      <Medal className="w-5 h-5" style={{ color: RANK_COLORS[idx] || "#94A3B8" }} />
                    </div>
                    <p className="text-xs font-mono-game mb-1" style={{ color: RANK_COLORS[idx] }}>#{idx + 1}</p>
                    <p className="text-sm font-bold text-[#F8FAFC] truncate">{l?.name || ""}</p>
                    <p className="font-mono-game text-lg font-bold text-[#00F0FF]">{l?.total_mined?.toLocaleString() || 0}</p>
                    <p className="text-[10px] text-[#64748B] uppercase">all-time</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Weekly Coins Rankings */}
          <div className="bg-[#131722] rounded-xl border border-[#FFD600]/20 overflow-hidden">
            <div className="p-4 border-b border-[#2A3241]/30 bg-[#FFD600]/5 flex items-center justify-between">
              <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD600]" /><span className="text-sm font-bold text-[#F8FAFC]">Weekly Coins Ranking</span></div>
              <span className="text-[10px] text-[#64748B]">Resets every week</span>
            </div>
            {weeklyRewards.length > 0 && (
              <div className="p-3 border-b border-[#2A3241]/20 flex flex-wrap gap-2">
                {weeklyRewards.slice(0, 5).map((r, i) => (
                  <span key={i} className="text-[10px] font-mono-game px-2 py-0.5 rounded bg-[#0B0E14] border border-[#2A3241]/30" style={{ color: RANK_COLORS[i] || "#94A3B8" }}>#{r.rank}: {r.coins}c</span>
                ))}
                {weeklyRewards.length > 5 && <span className="text-[10px] text-[#64748B]">+{weeklyRewards.length - 5} more</span>}
              </div>
            )}
            <div className="p-4 space-y-1 max-h-72 overflow-y-auto">
              {weeklyRankings.length > 0 ? weeklyRankings.map((r, i) => (
                <div key={r.user_id || `row-${i}`} className="flex items-center justify-between py-1.5 border-b border-[#2A3241]/15">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-game text-xs w-6" style={{ color: RANK_COLORS[i] || "#94A3B8" }}>#{i + 1}</span>
                    <span className="text-sm text-[#F8FAFC]">{r.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono-game text-xs text-[#FFD600]">{r.coins_earned?.toFixed(2)}</span>
                    {i < weeklyRewards.length && <p className="text-[9px] text-[#10B981]">Prize: {weeklyRewards[i].coins}c</p>}
                  </div>
                </div>
              )) : <p className="text-xs text-[#64748B] text-center py-4">No activity this week</p>}
            </div>
          </div>

          {/* Monthly Referrals Rankings */}
          <div className="bg-[#131722] rounded-xl border border-[#B026FF]/20 overflow-hidden">
            <div className="p-4 border-b border-[#2A3241]/30 bg-[#B026FF]/5 flex items-center justify-between">
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#B026FF]" /><span className="text-sm font-bold text-[#F8FAFC]">Monthly Referral Ranking</span></div>
              <span className="text-[10px] text-[#64748B]">Resets every month</span>
            </div>
            {monthlyRewards.length > 0 && (
              <div className="p-3 border-b border-[#2A3241]/20 flex flex-wrap gap-2">
                {monthlyRewards.slice(0, 5).map((r, i) => (
                  <span key={i} className="text-[10px] font-mono-game px-2 py-0.5 rounded bg-[#0B0E14] border border-[#2A3241]/30" style={{ color: RANK_COLORS[i] || "#94A3B8" }}>#{r.rank}: {r.coins}c</span>
                ))}
              </div>
            )}
            <div className="p-4 space-y-1 max-h-72 overflow-y-auto">
              {monthlyRefs.length > 0 ? monthlyRefs.map((r, i) => (
                <div key={r.user_id || `row-${i}`} className="flex items-center justify-between py-1.5 border-b border-[#2A3241]/15">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-game text-xs w-6" style={{ color: RANK_COLORS[i] || "#94A3B8" }}>#{i + 1}</span>
                    <span className="text-sm text-[#F8FAFC]">{r.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono-game text-xs text-[#B026FF]">{r.referrals} referrals</span>
                    {i < monthlyRewards.length && <p className="text-[9px] text-[#10B981]">Prize: {monthlyRewards[i].coins}c</p>}
                  </div>
                </div>
              )) : <p className="text-xs text-[#64748B] text-center py-4">No referrals this month</p>}
            </div>
          </div>
        </div>

        {/* Past Winners Archive — tabbed */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden">
          <div className="p-4 border-b border-[#2A3241]/30 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-sm font-bold text-[#F8FAFC]">Past Winners</span>
          </div>
          <div className="flex border-b border-[#2A3241]/30">
            <button onClick={() => setArchiveTab("weekly_coins")} data-testid="archive-tab-weekly"
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${archiveTab === "weekly_coins" ? "text-[#FFD600] border-b-2 border-[#FFD600] bg-[#FFD600]/5" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
              <Trophy className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Weekly ({weeklyArchive.length})
            </button>
            <button onClick={() => setArchiveTab("monthly_referrals")} data-testid="archive-tab-monthly"
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${archiveTab === "monthly_referrals" ? "text-[#B026FF] border-b-2 border-[#B026FF] bg-[#B026FF]/5" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
              <Users className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Monthly Referrals ({monthlyArchive.length})
            </button>
          </div>
          <div className="p-4">
            {(() => {
              const list = archiveTab === "weekly_coins" ? weeklyArchive : monthlyArchive;
              const accent = archiveTab === "weekly_coins" ? "#FFD600" : "#B026FF";
              const valueLabel = archiveTab === "weekly_coins" ? "coins earned" : "referrals";
              if (list.length === 0) {
                return <p className="text-xs text-[#64748B] text-center py-6">No {archiveTab === "weekly_coins" ? "weekly" : "monthly"} winners yet.</p>;
              }
              return (
                <div className="space-y-1">
                  {list.map((a, i) => (
                    <div key={a.label + i} className="flex items-center justify-between py-2 border-b border-[#2A3241]/15 last:border-0">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-[10px] text-[#64748B] font-mono-game w-6 shrink-0">#{i + 1}</span>
                        <span className="text-sm text-[#F8FAFC] truncate">{a.winner_name || "Anon"}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono-game text-xs" style={{ color: accent }}>+{a.winner_prize}c</p>
                        <p className="text-[10px] text-[#64748B]">{a.label} · {Number(a.winner_value || 0).toLocaleString()} {valueLabel}</p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </GameLayout>
  );
}
